import React, { FC, ReactNode } from 'react';

import styled from '@emotion/styled';
import { compose, layout, LayoutProps, BorderProps, border, space, SpaceProps } from '@lucid/theme';
import { Accordion as ReactAccordion } from 'react-accessible-accordion';

import AccordionButton, { AccordionButtonProps } from './accordion-button';
import AccordionHeading, { AccordionHeadingProps } from './accordion-heading';
import AccordionItem, { AccordionItemProps } from './accordion-item';
import AccordionPanel, { AccordionPanelProps } from './accordion-panel';
import decorate from '../decorate/decorate';

export interface AccordionSubComponents {
  /** The Button Subcomponent for Accordion. */
  Button: FC<AccordionButtonProps>;
  /** The Heading Subcomponent for Accordion. */
  Heading: FC<AccordionHeadingProps>;
  /** The Item Subcomponent for Accordion. */
  Item: FC<AccordionItemProps>;
  /** The Panel Subcomponent for Accordion. */
  Panel: FC<AccordionPanelProps>;
}

export interface AccordionProps extends BorderProps, LayoutProps, SpaceProps {
  /** Don't autocollapse items when expanding other items. */
  allowMultipleExpanded?: boolean;
  /** Allow the only remaining expanded item to be collapsed. */
  allowZeroExpanded?: boolean;
  /** The Accordion component children. */
  children?: ReactNode;
  /** Class(es) to apply to element. */
  className?: string;
  /** Accepts an array of strings and any `Accordion.Item`
   * whose uuid prop matches any one of these strings will be expanded on mount.
   */
  preExpanded?: string[];
  /** Callback which is invoked when items are expanded or collapsed.
   * Gets passed uuids of the currently expanded `Accordion.Item`s.
   */
  onChange?: (value: string[]) => void;
}

const accordionStyleProps = compose(border, layout, space);

const StyledAccordion = styled(ReactAccordion)<AccordionProps>(
  {
    label: 'accordion',
    flex: '0 1 100%',
    maxWidth: '100%',
  },
  accordionStyleProps,
);

const AccordionComponent: FC<AccordionProps> & AccordionSubComponents = ({ children, className, ...rest }) => (
  <StyledAccordion {...rest} className={className}>
    {children}
  </StyledAccordion>
);

AccordionComponent.Button = AccordionButton;
AccordionComponent.Heading = AccordionHeading;
AccordionComponent.Item = AccordionItem;
AccordionComponent.Panel = AccordionPanel;

// manually adding display name or it goes missing on build
AccordionComponent.displayName = 'Accordion';
const Accordion = decorate<AccordionProps, AccordionSubComponents>(AccordionComponent);
export { Accordion };
export default Accordion;
